// Store reactivo simple — sin React, sin dependencias.
//
// Decisión: pub-sub manual (no Proxy) porque la API expone una mutación
// explícita (`update`) y no queremos que asignaciones anidadas dispersas
// disparen renders. Más predecible para depurar y para los otros agentes.
//
// Persistencia automática en localStorage (clave 'isigenio-visualizador').
// El campo `step` NO se persiste: al recargar volvemos siempre al paso 1
// para que Berni vea el flujo entero, no a medio camino.

const STORAGE_KEY = 'isigenio-visualizador';

function defaultState() {
  return {
    step: 1, // 1..6
    room: { largo: 0, ancho: 0, altura: 0 },
    surfaces: {
      ceiling: 'Pladur (cartón yeso)',
      floor: 'Parquet',
      walls: [
        'Pladur (cartón yeso)',
        'Pladur (cartón yeso)',
        'Pared enfoscada',
        'Pared enfoscada',
      ],
    },
    product: null, // ISINACProduct o null
    panels: [], // [{ position, surface, ... }]
    results: null, // resultado del cálculo TR60
    client: { name: '', email: '', phone: '', city: '', notes: '' },
  };
}

function loadPersisted() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Nunca rehidratamos `step`.
    delete parsed.step;
    return parsed;
  } catch (err) {
    console.warn('[state] No se pudo leer localStorage:', err);
    return {};
  }
}

function persist(snapshot) {
  if (typeof localStorage === 'undefined') return;
  try {
    const { step: _step, ...rest } = snapshot;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  } catch (err) {
    console.warn('[state] No se pudo escribir en localStorage:', err);
  }
}

// Estado interno mutable. Se exporta `state` como referencia viva:
// los otros agentes pueden leer `state.room.largo` directamente.
export const state = Object.assign(defaultState(), loadPersisted());

const subscribers = new Set();

export function subscribe(callback) {
  if (typeof callback !== 'function') {
    throw new TypeError('subscribe espera una función');
  }
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

// Mutación profunda superficial: para `room`, `surfaces`, `client` admite
// patches parciales (merge a 1 nivel). Para todo lo demás reemplaza.
export function update(patch) {
  if (!patch || typeof patch !== 'object') return;

  for (const [key, value] of Object.entries(patch)) {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      state[key] &&
      typeof state[key] === 'object' &&
      !Array.isArray(state[key])
    ) {
      Object.assign(state[key], value);
    } else {
      state[key] = value;
    }
  }

  persist(state);

  for (const cb of subscribers) {
    try {
      cb(state);
    } catch (err) {
      console.error('[state] Error en suscriptor:', err);
    }
  }
}

// Utilidad: borrar todo y volver a por defecto. Útil al cerrar un
// presupuesto y empezar otro.
export function reset() {
  const fresh = defaultState();
  for (const key of Object.keys(state)) delete state[key];
  Object.assign(state, fresh);
  persist(state);
  for (const cb of subscribers) {
    try {
      cb(state);
    } catch (err) {
      console.error('[state] Error en suscriptor:', err);
    }
  }
}
